import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    TextField,
    IconButton,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Collapse,
    Alert,
    LinearProgress,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tabs,
    Tab,
    Breadcrumbs,
    Link
} from '@mui/material';

// icons
import {
    IconDatabase,
    IconTable,
    IconChevronRight,
    IconChevronDown,
    IconPlus,
    IconEdit,
    IconTrash,
    IconSearch,
    IconRefresh,
    IconDownload,
    IconUpload,
    IconCopy,
    IconFilter,
    IconSortAscending,
    IconEye,
    IconSettings,
    IconX
} from '@tabler/icons-react';

const CollectionsBrowser = () => {
    const theme = useTheme();
    const [selectedDatabase, setSelectedDatabase] = useState('production_db');
    const [selectedCollection, setSelectedCollection] = useState(null);
    const [expandedDatabases, setExpandedDatabases] = useState({ production_db: true });
    const [searchTerm, setSearchTerm] = useState('');
    const [tabValue, setTabValue] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [createDialog, setCreateDialog] = useState(false);
    const [importDialog, setImportDialog] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);

    // Mock data for databases and collections
    const databases = [
        {
            name: 'production_db',
            collections: [
                { name: 'users', count: 15420, size: '45.2 MB', avgDocSize: '3.1 KB', indexes: 5 },
                { name: 'orders', count: 48392, size: '128.7 MB', avgDocSize: '2.7 KB', indexes: 7 },
                { name: 'products', count: 3847, size: '22.4 MB', avgDocSize: '5.8 KB', indexes: 4 },
                { name: 'sessions', count: 125837, size: '312.5 MB', avgDocSize: '2.5 KB', indexes: 3 },
                { name: 'logs', count: 1847293, size: '2.4 GB', avgDocSize: '1.3 KB', indexes: 2 }
            ],
            stats: {
                dataSize: '2.9 GB',
                storageSize: '3.2 GB',
                indexes: 21,
                avgObjSize: '2.8 KB'
            }
        },
        {
            name: 'analytics_db',
            collections: [
                { name: 'events', count: 847293, size: '1.2 GB', avgDocSize: '1.5 KB', indexes: 4 },
                { name: 'metrics', count: 293847, size: '423.1 MB', avgDocSize: '1.4 KB', indexes: 3 },
                { name: 'reports', count: 1247, size: '18.3 MB', avgDocSize: '14.7 KB', indexes: 2 }
            ],
            stats: {
                dataSize: '1.6 GB',
                storageSize: '1.8 GB',
                indexes: 9,
                avgObjSize: '2.1 KB'
            }
        }
    ];

    // Mock documents for selected collection
    const sampleDocuments = [
        {
            _id: '65a1234567890abcdef12345',
            name: 'John Doe',
            email: 'john.doe@example.com',
            role: 'admin',
            created_at: new Date('2024-01-15').toISOString(),
            updated_at: new Date('2024-01-16').toISOString()
        },
        {
            _id: '65a1234567890abcdef12346',
            name: 'Jane Smith',
            email: 'jane.smith@example.com',
            role: 'user',
            created_at: new Date('2024-01-14').toISOString(),
            updated_at: new Date('2024-01-14').toISOString()
        }
    ];

    const handleDatabaseExpand = (dbName) => {
        setExpandedDatabases((prev) => ({
            ...prev,
            [dbName]: !prev[dbName]
        }));
    };

    const handleCollectionSelect = (db, collection) => {
        setSelectedDatabase(db);
        setSelectedCollection(collection);
    };

    const renderDocumentValue = (value) => {
        if (value === null) return <Chip label="null" size="small" />;
        if (value === undefined) return <Chip label="undefined" size="small" />;
        if (typeof value === 'boolean') return <Chip label={value.toString()} size="small" color={value ? 'success' : 'error'} />;
        if (typeof value === 'object')
            return (
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    {JSON.stringify(value, null, 2)}
                </Typography>
            );
        return value;
    };

    return (
        <Box>
            <Grid container spacing={3}>
                {/* Left Panel - Database/Collection Tree */}
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h5">Databases</Typography>
                                <IconButton size="small" onClick={() => setCreateDialog(true)}>
                                    <IconPlus />
                                </IconButton>
                            </Box>

                            <TextField
                                fullWidth
                                placeholder="Search collections..."
                                size="small"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={{ mb: 2 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconSearch size={16} />
                                        </InputAdornment>
                                    )
                                }}
                            />

                            <List dense>
                                {databases.map((db) => (
                                    <Box key={db.name}>
                                        <ListItem button onClick={() => handleDatabaseExpand(db.name)} sx={{ pl: 0 }}>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                {expandedDatabases[db.name] ? (
                                                    <IconChevronDown size={18} />
                                                ) : (
                                                    <IconChevronRight size={18} />
                                                )}
                                            </ListItemIcon>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <IconDatabase size={18} />
                                            </ListItemIcon>
                                            <ListItemText primary={db.name} secondary={`${db.collections.length} collections`} />
                                        </ListItem>
                                        <Collapse in={expandedDatabases[db.name]} timeout="auto" unmountOnExit>
                                            <List component="div" disablePadding>
                                                {db.collections
                                                    .filter((col) => col.name.toLowerCase().includes(searchTerm.toLowerCase()))
                                                    .map((collection) => (
                                                        <ListItem
                                                            key={collection.name}
                                                            button
                                                            sx={{ pl: 6 }}
                                                            selected={
                                                                selectedDatabase === db.name && selectedCollection?.name === collection.name
                                                            }
                                                            onClick={() => handleCollectionSelect(db.name, collection)}
                                                        >
                                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                                <IconTable size={16} />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={collection.name}
                                                                secondary={`${collection.count.toLocaleString()} docs`}
                                                            />
                                                        </ListItem>
                                                    ))}
                                            </List>
                                        </Collapse>
                                    </Box>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Panel - Collection Details */}
                <Grid item xs={12} md={9}>
                    {selectedCollection ? (
                        <Card>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <Box sx={{ p: 2 }}>
                                    <Breadcrumbs>
                                        <Link color="inherit" href="#" onClick={() => setSelectedCollection(null)}>
                                            Databases
                                        </Link>
                                        <Link color="inherit" href="#">
                                            {selectedDatabase}
                                        </Link>
                                        <Typography color="text.primary">{selectedCollection.name}</Typography>
                                    </Breadcrumbs>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                        <Box>
                                            <Typography variant="h4">{selectedCollection.name}</Typography>
                                            <Box display="flex" gap={2} mt={1}>
                                                <Chip label={`${selectedCollection.count.toLocaleString()} documents`} size="small" />
                                                <Chip label={`Size: ${selectedCollection.size}`} size="small" />
                                                <Chip label={`${selectedCollection.indexes} indexes`} size="small" />
                                                <Chip label={`Avg: ${selectedCollection.avgDocSize}`} size="small" />
                                            </Box>
                                        </Box>
                                        <Box display="flex" gap={1}>
                                            <Button size="small" startIcon={<IconPlus />}>
                                                Insert
                                            </Button>
                                            <Button size="small" startIcon={<IconUpload />}>
                                                Import
                                            </Button>
                                            <Button size="small" startIcon={<IconDownload />}>
                                                Export
                                            </Button>
                                            <IconButton size="small">
                                                <IconSettings />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                </Box>
                                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                                    <Tab label="Documents" />
                                    <Tab label="Indexes" />
                                    <Tab label="Schema Analyzer" />
                                    <Tab label="Statistics" />
                                </Tabs>
                            </Box>

                            <CardContent>
                                {/* Documents Tab */}
                                {tabValue === 0 && (
                                    <>
                                        <Box display="flex" gap={2} mb={2}>
                                            <TextField
                                                placeholder="Filter documents..."
                                                size="small"
                                                sx={{ flexGrow: 1 }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <IconFilter size={16} />
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                                <InputLabel>Sort by</InputLabel>
                                                <Select label="Sort by" defaultValue="_id">
                                                    <MenuItem value="_id">_id</MenuItem>
                                                    <MenuItem value="created_at">Created Date</MenuItem>
                                                    <MenuItem value="updated_at">Updated Date</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <IconButton size="small">
                                                <IconSortAscending />
                                            </IconButton>
                                            <IconButton size="small" color="primary">
                                                <IconRefresh />
                                            </IconButton>
                                        </Box>

                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>_id</TableCell>
                                                        <TableCell>name</TableCell>
                                                        <TableCell>email</TableCell>
                                                        <TableCell>role</TableCell>
                                                        <TableCell>created_at</TableCell>
                                                        <TableCell align="right">Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {sampleDocuments.map((doc) => (
                                                        <TableRow key={doc._id} hover>
                                                            <TableCell>
                                                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                                    {doc._id.substring(0, 12)}...
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>{doc.name}</TableCell>
                                                            <TableCell>{doc.email}</TableCell>
                                                            <TableCell>
                                                                <Chip label={doc.role} size="small" />
                                                            </TableCell>
                                                            <TableCell>{new Date(doc.created_at).toLocaleDateString()}</TableCell>
                                                            <TableCell align="right">
                                                                <IconButton size="small" onClick={() => setSelectedDocument(doc)}>
                                                                    <IconEye size={16} />
                                                                </IconButton>
                                                                <IconButton size="small">
                                                                    <IconEdit size={16} />
                                                                </IconButton>
                                                                <IconButton size="small" color="error">
                                                                    <IconTrash size={16} />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                            <TablePagination
                                                component="div"
                                                count={selectedCollection.count}
                                                page={page}
                                                onPageChange={(e, newPage) => setPage(newPage)}
                                                rowsPerPage={rowsPerPage}
                                                onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                                            />
                                        </TableContainer>
                                    </>
                                )}

                                {/* Indexes Tab */}
                                {tabValue === 1 && (
                                    <Box>
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            Indexes improve query performance. Create indexes on fields that are frequently queried.
                                        </Alert>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Name</TableCell>
                                                        <TableCell>Keys</TableCell>
                                                        <TableCell>Type</TableCell>
                                                        <TableCell>Size</TableCell>
                                                        <TableCell>Usage</TableCell>
                                                        <TableCell align="right">Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell>_id_</TableCell>
                                                        <TableCell>
                                                            <Chip label="_id: 1" size="small" />
                                                        </TableCell>
                                                        <TableCell>Single Field</TableCell>
                                                        <TableCell>2.1 MB</TableCell>
                                                        <TableCell>
                                                            <LinearProgress variant="determinate" value={87} sx={{ width: 100 }} />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Chip label="System" size="small" />
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell>email_1</TableCell>
                                                        <TableCell>
                                                            <Chip label="email: 1" size="small" />
                                                        </TableCell>
                                                        <TableCell>Unique</TableCell>
                                                        <TableCell>1.8 MB</TableCell>
                                                        <TableCell>
                                                            <LinearProgress variant="determinate" value={65} sx={{ width: 100 }} />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <IconButton size="small" color="error">
                                                                <IconTrash size={16} />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <Box display="flex" justifyContent="flex-end" mt={2}>
                                            <Button variant="contained" startIcon={<IconPlus />}>
                                                Create Index
                                            </Button>
                                        </Box>
                                    </Box>
                                )}

                                {/* Schema Analyzer Tab */}
                                {tabValue === 2 && (
                                    <Box>
                                        <Alert severity="success" sx={{ mb: 2 }}>
                                            Schema analysis complete. Found 6 fields with consistent types across all documents.
                                        </Alert>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom>
                                                            Field Analysis
                                                        </Typography>
                                                        <List>
                                                            <ListItem>
                                                                <ListItemText primary="_id" secondary="ObjectId • 100% present • Unique" />
                                                                <Chip label="Required" size="small" color="primary" />
                                                            </ListItem>
                                                            <ListItem>
                                                                <ListItemText
                                                                    primary="name"
                                                                    secondary="String • 100% present • Avg length: 18 chars"
                                                                />
                                                            </ListItem>
                                                            <ListItem>
                                                                <ListItemText
                                                                    primary="email"
                                                                    secondary="String • 100% present • Valid emails"
                                                                />
                                                                <Chip label="Indexed" size="small" />
                                                            </ListItem>
                                                            <ListItem>
                                                                <ListItemText
                                                                    primary="role"
                                                                    secondary="String • 100% present • 3 unique values"
                                                                />
                                                            </ListItem>
                                                        </List>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom>
                                                            Recommended Schema
                                                        </Typography>
                                                        <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                                                            <Typography
                                                                variant="body2"
                                                                component="pre"
                                                                sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                                            >
                                                                {`{
  "_id": ObjectId,
  "name": {
    "type": "string",
    "required": true,
    "maxLength": 100
  },
  "email": {
    "type": "string",
    "required": true,
    "unique": true,
    "pattern": "email"
  },
  "role": {
    "type": "string",
    "enum": ["admin", "user", "guest"]
  },
  "created_at": {
    "type": "date",
    "default": "now"
  },
  "updated_at": {
    "type": "date",
    "default": "now"
  }
}`}
                                                            </Typography>
                                                        </Paper>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}

                                {/* Statistics Tab */}
                                {tabValue === 3 && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom>
                                                        Collection Stats
                                                    </Typography>
                                                    <List>
                                                        <ListItem>
                                                            <ListItemText
                                                                primary="Documents"
                                                                secondary={selectedCollection.count.toLocaleString()}
                                                            />
                                                        </ListItem>
                                                        <ListItem>
                                                            <ListItemText primary="Total Size" secondary={selectedCollection.size} />
                                                        </ListItem>
                                                        <ListItem>
                                                            <ListItemText
                                                                primary="Average Size"
                                                                secondary={selectedCollection.avgDocSize}
                                                            />
                                                        </ListItem>
                                                        <ListItem>
                                                            <ListItemText primary="Indexes" secondary={selectedCollection.indexes} />
                                                        </ListItem>
                                                    </List>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom>
                                                        Growth Trend
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            height: 300,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <Typography color="textSecondary">Chart visualization would go here</Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                )}
                            </CardContent>
                        </Card>
                    ) : (
                        <Card sx={{ height: '100%', minHeight: 600 }}>
                            <CardContent
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <IconDatabase size={64} style={{ opacity: 0.3, marginBottom: 16 }} />
                                <Typography variant="h5" gutterBottom sx={{ opacity: 0.7 }}>
                                    Select a Collection
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Choose a collection from the left panel to view and manage its documents
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Grid>
            </Grid>

            {/* Create Collection Dialog */}
            <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New Collection</DialogTitle>
                <DialogContent>
                    <TextField fullWidth label="Database" select defaultValue="production_db" sx={{ mt: 2, mb: 2 }}>
                        {databases.map((db) => (
                            <MenuItem key={db.name} value={db.name}>
                                {db.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField fullWidth label="Collection Name" placeholder="my_collection" sx={{ mb: 2 }} />
                    <Alert severity="info">The collection will be created when the first document is inserted.</Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => setCreateDialog(false)}>
                        Create Collection
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Document Details Dialog */}
            <Dialog open={!!selectedDocument} onClose={() => setSelectedDocument(null)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Document Details
                    <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setSelectedDocument(null)}>
                        <IconX />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedDocument && (
                        <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                            <Typography
                                variant="body2"
                                component="pre"
                                sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem',
                                    whiteSpace: 'pre-wrap'
                                }}
                            >
                                {JSON.stringify(selectedDocument, null, 2)}
                            </Typography>
                        </Paper>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button startIcon={<IconCopy />}>Copy</Button>
                    <Button startIcon={<IconEdit />}>Edit</Button>
                    <Button onClick={() => setSelectedDocument(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CollectionsBrowser;
